
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  User, 
  Phone, 
  Mail, 
  Calendar, 
  MapPin, 
  Building, 
  DollarSign, 
  Clock, 
  FileText,
  GraduationCap,
  Briefcase,
  Award,
  IdCard
} from "lucide-react";
import { Employee } from "@/types/employee";

interface EmployeeDetailsViewProps {
  employee: Employee;
}

export const EmployeeDetailsView = ({ employee }: EmployeeDetailsViewProps) => {
  const getStatusBadge = (status: string) => {
    switch (status) {
      case "present":
        return <Badge className="bg-emerald-100 text-emerald-700">حاضر</Badge>;
      case "absent":
        return <Badge className="bg-red-100 text-red-700">غائب</Badge>;
      case "late":
        return <Badge className="bg-amber-100 text-amber-700">متأخر</Badge>;
      default:
        return <Badge className="bg-slate-100 text-slate-700">غير معروف</Badge>;
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "غير محدد";
    return new Date(dateString).toLocaleDateString('ar-SA');
  };

  const formatLastSeen = (lastSeen: string | null) => {
    if (!lastSeen) return "غير محدد";
    const date = new Date(lastSeen);
    const today = new Date();
    const isToday = date.toDateString() === today.toDateString();
    
    if (isToday) {
      return `${date.toLocaleTimeString('ar-SA', { hour: '2-digit', minute: '2-digit' })} اليوم`;
    } else {
      return date.toLocaleDateString('ar-SA');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header Card */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center gap-6">
            <Avatar className="w-20 h-20">
              {employee.profile_image && (
                <AvatarImage src={employee.profile_image} alt={employee.name} />
              )}
              <AvatarFallback className="bg-gradient-to-r from-blue-600 to-emerald-600 text-white font-semibold text-lg">
                {employee.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1">
              <h2 className="text-2xl font-bold text-slate-800">{employee.name}</h2>
              <p className="text-lg text-slate-600">{employee.job_title || employee.position}</p>
              <div className="flex items-center gap-4 mt-2">
                {getStatusBadge(employee.status)}
                <span className="text-sm text-slate-500">
                  آخر ظهور: {formatLastSeen(employee.last_seen)}
                </span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Detailed Information Tabs */}
      <Tabs defaultValue="basic" className="w-full">
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="basic">المعلومات الأساسية</TabsTrigger>
          <TabsTrigger value="professional">المعلومات المهنية</TabsTrigger>
          <TabsTrigger value="qualifications">المؤهلات والخبرة</TabsTrigger>
          <TabsTrigger value="financial">المعلومات المالية</TabsTrigger>
          <TabsTrigger value="documents">المستندات</TabsTrigger>
        </TabsList>

        <TabsContent value="basic" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="w-5 h-5" />
                المعلومات الشخصية
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-slate-500" />
                  <span className="font-medium">الجوال:</span>
                  <span dir="ltr">{employee.phone || 'غير محدد'}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-slate-500" />
                  <span className="font-medium">البريد الإلكتروني:</span>
                  <span dir="ltr">{employee.email || 'غير محدد'}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-slate-500" />
                  <span className="font-medium">تاريخ الميلاد:</span>
                  <span>{formatDate(employee.birth_date)}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Building className="w-4 h-4 text-slate-500" />
                  <span className="font-medium">القسم:</span>
                  <div className="flex flex-wrap gap-1">
                    {(employee.departments && employee.departments.length > 0) ? (
                      employee.departments.map((dep) => (
                        <Badge key={dep.id} variant="secondary">{dep.name}</Badge>
                      ))
                    ) : (
                      <Badge variant="outline">{employee.department || 'غير محدد'}</Badge>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="professional" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Briefcase className="w-5 h-5" />
                المعلومات المهنية
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center gap-2">
                  <IdCard className="w-4 h-4 text-slate-500" />
                  <span className="font-medium">رقم الموظف:</span>
                  <span>{employee.employee_number || 'غير محدد'}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-slate-500" />
                  <span className="font-medium">تاريخ التوظيف:</span>
                  <span>{formatDate(employee.hiring_date)}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-slate-500" />
                  <span className="font-medium">ساعات العمل:</span>
                  <span>{employee.working_hours || 8} ساعة يومياً</span>
                </div>
              </div>
              
              {employee.job_description && (
                <div className="space-y-2">
                  <h4 className="font-medium flex items-center gap-2">
                    <FileText className="w-4 h-4 text-slate-500" />
                    الوصف الوظيفي:
                  </h4>
                  <p className="text-slate-600 bg-slate-50 p-3 rounded-lg">{employee.job_description}</p>
                </div>
              )}
              
              {employee.job_responsibilities && (
                <div className="space-y-2">
                  <h4 className="font-medium flex items-center gap-2">
                    <Award className="w-4 h-4 text-slate-500" />
                    المسؤوليات الوظيفية:
                  </h4>
                  <p className="text-slate-600 bg-slate-50 p-3 rounded-lg">{employee.job_responsibilities}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="qualifications" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <GraduationCap className="w-5 h-5" />
                المؤهلات والخبرة
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {employee.qualifications && (
                <div className="space-y-2">
                  <h4 className="font-medium">المؤهلات:</h4>
                  <p className="text-slate-600 bg-slate-50 p-3 rounded-lg">{employee.qualifications}</p>
                </div>
              )}
              
              {employee.previous_experience && (
                <div className="space-y-2">
                  <h4 className="font-medium">الخبرات السابقة:</h4>
                  <p className="text-slate-600 bg-slate-50 p-3 rounded-lg">{employee.previous_experience}</p>
                </div>
              )}
              
              {employee.electronic_accounts && (
                <div className="space-y-2">
                  <h4 className="font-medium">الحسابات الإلكترونية:</h4>
                  <div className="bg-slate-50 p-3 rounded-lg">
                    <pre className="text-sm text-slate-600">
                      {typeof employee.electronic_accounts === 'string' 
                        ? employee.electronic_accounts 
                        : JSON.stringify(employee.electronic_accounts, null, 2)}
                    </pre>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="financial" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="w-5 h-5" />
                المعلومات المالية
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center gap-2">
                  <DollarSign className="w-4 h-4 text-slate-500" />
                  <span className="font-medium">الراتب:</span>
                  <span>{employee.salary ? `${employee.salary} ريال` : 'غير محدد'}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="documents" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                المستندات المرفقة
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {employee.certificate_file && (
                  <div className="flex flex-col items-center p-4 border rounded-lg">
                    <GraduationCap className="w-8 h-8 text-blue-500 mb-2" />
                    <span className="text-sm font-medium">الشهادة</span>
                    <span className="text-xs text-green-600">متوفر</span>
                  </div>
                )}
                
                {employee.national_id_file && (
                  <div className="flex flex-col items-center p-4 border rounded-lg">
                    <IdCard className="w-8 h-8 text-green-500 mb-2" />
                    <span className="text-sm font-medium">الهوية الوطنية</span>
                    <span className="text-xs text-green-600">متوفر</span>
                  </div>
                )}
                
                {employee.contract_file && (
                  <div className="flex flex-col items-center p-4 border rounded-lg">
                    <FileText className="w-8 h-8 text-amber-500 mb-2" />
                    <span className="text-sm font-medium">العقد</span>
                    <span className="text-xs text-green-600">متوفر</span>
                  </div>
                )}
                
                {!employee.certificate_file && !employee.national_id_file && !employee.contract_file && (
                  <div className="col-span-full text-center text-slate-500">
                    لا توجد مستندات مرفقة
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};
